/**
 * Forward-compatibility type shims for the upcoming fansunited-frontend-core betslip types.
 *
 * These mirror the interfaces from fansunited-frontend-core >= 0.0.65.
 * TODO(betslip-library-swap): Once fansunited-frontend-core is upgraded to a version
 * that exports BetslipLabels, BetslipProps, BetslipState, and BetslipSelectionInput,
 * remove this file and import directly:
 *   import type { BetslipLabels, BetslipProps, BetslipState, BetslipSelectionInput } from "fansunited-frontend-core";
 */

import type { CustomThemeOptions } from "fansunited-frontend-core";
import type { BetslipPosition } from "@/lib/tenant/TenantSettingsProvider";

/**
 * Mirrors the shape of BetslipSelectionInput from fansunited-frontend-core >= 0.0.85.
 * The library does NOT put a derived `id` on selection objects; the composite key
 * "eventId:market:outcome" is used as the selection identity by betslipApi.setSelection().
 *
 * Only the fields relevant to canvas-side persistence are listed here.
 * The real type has additional display fields (participantOne, competition, etc.).
 */
export interface BetslipSelectionInput {
    eventId: string;
    market: string;
    outcome: string;
    startAt?: string;
}

export interface BetslipState {
    selections: BetslipSelectionInput[];
    stake: number;
    totalOdds: number;
}

/**
 * Derive the selection's composite key (matches betslipApi.setSelection's id parameter).
 * Format: "eventId:market:outcome"
 */
export function selectionId(s: BetslipSelectionInput): string {
    return `${s.eventId}:${s.market}:${s.outcome}`;
}

export interface BetslipOutcomeLabels {
    yes?: string;
    no?: string;
    draw?: string;
    over?: string;
    under?: string;
    noGoal?: string;
    nobody?: string;
    or?: string;
}

export interface BetslipLabels {
    title?: string;
    placeBetLabel?: string;
    emptyTitle?: string;
    emptyDescription?: string;
    continueLabel?: string;
    disclaimer?: string;
    combinedOddsLabel?: string;
    stakeLabel?: string;
    totalStakeLabel?: string;
    potentialWinLabel?: string;
    oddsUnavailableLabel?: string;
    suspendedLabel?: string;
    outcomeLabels?: BetslipOutcomeLabels;
    marketLabels?: Record<string, string>;
}

export interface BetslipProps {
    /** FansUnited SDK instance. Supplied by the mount wrapper. */
    sdk?: unknown;
    /** BCP-47 language tag. Supplied by the mount wrapper. */
    language?: string;
    position?: BetslipPosition;
    maxSelections?: number;
    oddsPollingInterval?: number;
    currency?: string;
    stakePresets?: number[];
    ctaUrlTemplate?: string;
    brandingLogoUrl?: string;
    operatorId?: string;
    affiliateId?: string;
    labels?: BetslipLabels;
    themeOptions?: CustomThemeOptions;
}

// --- CompetitorCard ---
// Same gap as the Betslip types above: fansunited-frontend-core 0.0.88 defines
// CompetitorCardProps + CompetitorCardLabels in interfaces/interfaces.d.ts but
// does not re-export them from the package root. The currently installed version
// (0.0.44) does not yet include these types, so they are hand-rolled here.
// TODO: when `fansunited-frontend-core` publishes these at the top level,
// delete this block and import from the package root instead.
export interface CompetitorCardLabels {
    [key: string]: string | undefined;
}

export interface CompetitorCardProps {
    language?: string;
    themeOptions?: CustomThemeOptions;
    labels?: CompetitorCardLabels;
    /** When false, suppresses operator / bookmaker branding on the card. Default true. */
    hasBranding?: boolean;
}
